import React, { useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

import { usePageDetails } from "../../hooks/usePageDetails";
import CreateHeading from "../../components/CreateHeading/CreateHeading";
import { Layout } from "../../layout/PortalLayout/PortalLayout";
import FormInput from "../../components/FormInput/FormInput";
import ToggleSwitch from "../../components/ToggleSwitch/ToggleSwitch";
import FormActionButtons from "../../components/FormActionButtons/FormActionButtons";
import {
  useCreateFirmwareMutation,
  useEditFirmwareMutation,
  useLazyGetSingleFirmwareDetailsQuery,
} from "../../app/api/firmwareApi";
import AsyncFormSelect from "../../components/FormSelect/AsyncFormSelect";
import { useLazyGetFamilyListQuery } from "../../app/api/familyApi";
import { useLazyGetBrandListQuery } from "../../app/api/brandApi";
import { handleApiResponse } from "../../utils/handleApiResponse";
import { useFilterManager } from "../../hooks/useFilterManager";
import { useAppSelector } from "../../redux/store";

export interface InitialFormValues {
  brand_id: string | null;
  family_id: string | null;
  provisioning_url: string;
  firmware_version: string;
  is_active: boolean;
}

const validationSchema = Yup.object().shape({
  is_active: Yup.boolean(),
  family_id: Yup.string().required("Family is required"),
  brand_id: Yup.string().required("Brand is required"),
  provisioning_url: Yup.string()
    .required("Provisioing URL is required")
    .url("Invalid URL format"),
  firmware_version: Yup.string()
    .required("Firmware Version is required")
    .min(2, "Firmware Version must be at least 2 character")
    .max(255, "Firmware Version must be at most 255 characters"),
});

const CreateFirmware: React.FC = () => {
  const { id, pageType } = usePageDetails();
  const { handleFilterChange } = useFilterManager();
  const [getFamilyListQuery] = useLazyGetFamilyListQuery();
  const [getBrandListQuery] = useLazyGetBrandListQuery();
  const [createFirmwareMutation] = useCreateFirmwareMutation();
  const [getSingleFirmwareDetailsQuery, getSingleFirmwareDetailsResponse] =
    useLazyGetSingleFirmwareDetailsQuery();
  const [editFirmwareMutation] = useEditFirmwareMutation();

  const persistDropdownSelector = useAppSelector(
    (state) => state.dropdownSelector
  );

  const navigate = useNavigate();

  const initialValues: InitialFormValues = {
    is_active: getSingleFirmwareDetailsResponse?.data?.data?.is_active ?? true,
    family_id: persistDropdownSelector["family_selector"]?.value ?? "",
    brand_id: persistDropdownSelector["brand_selector"]?.value ?? "",
    provisioning_url:
      getSingleFirmwareDetailsResponse?.data?.data?.provisioning_url ?? "",
    firmware_version:
      getSingleFirmwareDetailsResponse?.data?.data?.firmware_version ?? "",
  };

  const getSingleFirmwareDetails = async () => {
    try {
      await getSingleFirmwareDetailsQuery({
        brand_id: persistDropdownSelector["brand_selector"]?.value,
        family_id: persistDropdownSelector["family_selector"]?.value,
        firmware_id: id,
      }).unwrap();
    } catch (error: any) {
      console.log(error);
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Get Firmware Details Error",
      });
    }
  };

  useEffect(() => {
    if (pageType !== "create") {
      getSingleFirmwareDetails();
    }
    // eslint-disable-next-line
  }, [id]);

  const handleSubmit = async (
    values: InitialFormValues,
    { setFieldError }: FormikHelpers<InitialFormValues>
  ) => {
    const {
      brand_id,
      family_id,
      firmware_version,
      is_active,
      provisioning_url,
    } = values;

    const body = {
      firmware_version,
      provisioning_url,
      ...(pageType === "edit" ? { is_active } : {}),
    };

    const successMessage =
      pageType === "create"
        ? "Firmware created successfully."
        : "Firmware updated successfully.";

    try {
      const response =
        pageType === "create"
          ? await createFirmwareMutation({ body, brand_id, family_id }).unwrap()
          : await editFirmwareMutation({
              body,
              brand_id,
              family_id,
              firmware_id: id,
            }).unwrap();
      if (response?.status_code === 200 || response?.status_code === 201) {
        handleApiResponse({
          type: "success",
          message: successMessage,
          toastId: "Fetch Firmware List Error",
        });
        navigate("/provisioning/firmware");
      }
    } catch (error: any) {
      handleApiResponse({
        response: error,
        type: "error",
        toastId: "Create Firmware Error",
        setFieldError,
      });

      console.log(error);
    }
  };

  return (
    <Layout>
      <CreateHeading label={"Firmware"} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
          dirty,
        }) => {
          const disable =
            pageType === "create"
              ? !values.firmware_version ||
                !values.provisioning_url ||
                !values.brand_id ||
                !values.family_id
              : !dirty;

          return (
            <>
              <Form className="w-100 mb-5 pb-5">
                <Col sm={6}>
                  {pageType === "view" && (
                    <Row className="my-2">
                      <FormInput
                        label="Firmware ID"
                        type="text"
                        name="id"
                        placeholder="Enter firmware name"
                        labelClassName="m-0 fw-500"
                        controlClassName="bg-white fs-15-point-5 h-48"
                        value={
                          getSingleFirmwareDetailsResponse?.data?.data?.id ?? ""
                        }
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required={false}
                        isValid={
                          touched.provisioning_url && !errors.provisioning_url
                        }
                        isInvalid={
                          touched.provisioning_url && !!errors.provisioning_url
                        }
                        touched={touched.provisioning_url}
                        error={errors.provisioning_url}
                        disabled
                      />
                    </Row>
                  )}

                  {pageType === "create" ? (
                    <>
                      {" "}
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Brand"
                          name="brand_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "brand_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getBrandListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                          }}
                          optionLabelName="brand_name"
                          placeholder="Select Brand"
                          shouldFetch
                          persistedOption={
                            persistDropdownSelector["brand_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["brand_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["brand_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.brand_id}
                          error={errors.brand_id}
                          setFieldTouched={setFieldTouched}
                        />
                      </Row>
                      <Row className="my-2">
                        <AsyncFormSelect
                          label="Family"
                          name="family_id"
                          onChange={(val: string | null, label: string) => {
                            handleFilterChange({
                              updatedField: "family_id",
                              persistedSelection: {
                                label,
                                value: val,
                              },
                            });
                          }}
                          fetchQuery={getFamilyListQuery}
                          fetchQueryParams={{
                            startKey: "",
                            page_size: 5,
                            brand_id: values.brand_id,
                          }}
                          optionLabelName="family_name"
                          placeholder="Select Family"
                          isDisabled={!values.brand_id}
                          key={values.brand_id}
                          shouldFetch={!!values.brand_id?.length}
                          persistedOption={
                            persistDropdownSelector["family_selector"]
                              ? {
                                  label:
                                    persistDropdownSelector["family_selector"]
                                      ?.label,
                                  value:
                                    persistDropdownSelector["family_selector"]
                                      ?.value,
                                }
                              : null
                          }
                          touched={touched.family_id}
                          error={errors.family_id}
                          setFieldTouched={setFieldTouched}
                        />
                      </Row>
                    </>
                  ) : (
                    <>
                      {" "}
                      <Row className="my-2">
                        <FormInput
                          label="Brand Name"
                          type="text"
                          name="brand_name"
                          placeholder="Brand name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFirmwareDetailsResponse?.data?.data
                              ?.brand_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                      <Row className="my-2">
                        <FormInput
                          label="Family Name"
                          type="text"
                          name="family_name"
                          placeholder="Family name"
                          labelClassName="m-0 fw-500"
                          controlClassName="bg-white fs-15-point-5 h-48"
                          value={
                            getSingleFirmwareDetailsResponse?.data?.data
                              ?.family_name ?? ""
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          required={true}
                          isValid={false}
                          isInvalid={false}
                          touched={false}
                          error={""}
                          disabled
                        />
                      </Row>
                    </>
                  )}

                  <Row className="my-2">
                    <FormInput
                      label="Provisioning URL"
                      type="text"
                      name="provisioning_url"
                      placeholder="Enter provisioning url"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.provisioning_url}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={
                        touched.provisioning_url && !errors.provisioning_url
                      }
                      isInvalid={
                        touched.provisioning_url && !!errors.provisioning_url
                      }
                      touched={touched.provisioning_url}
                      error={errors.provisioning_url}
                      disabled={pageType === "view"}
                    />
                  </Row>
                  <Row className="my-2">
                    <FormInput
                      label="Firmware Version"
                      type="text"
                      name="firmware_version"
                      placeholder="Enter provisioning url"
                      labelClassName="m-0 fw-500"
                      controlClassName="bg-white fs-15-point-5 h-48"
                      value={values.firmware_version}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required={true}
                      isValid={
                        touched.firmware_version && !errors.firmware_version
                      }
                      isInvalid={
                        touched.firmware_version && !!errors.firmware_version
                      }
                      touched={touched.firmware_version}
                      error={errors.firmware_version}
                      disabled={pageType === "view"}
                    />
                  </Row>

                  {pageType !== "create" && (
                    <Row className="ms-0 mt-4">
                      <ToggleSwitch
                        activeStatusText="Active"
                        inActiveStatusText="Inactive"
                        value={values.is_active}
                        onChange={(val: boolean) => {
                          setFieldValue("is_active", val);
                        }}
                        key={String(values.is_active)}
                        isDisabled={pageType === "view"}
                      />
                    </Row>
                  )}

                  <FormActionButtons
                    disable={disable}
                    isSubmitting={isSubmitting}
                    navigateUrl="/provisioning/firmware"
                  />
                </Col>
              </Form>
            </>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default CreateFirmware;
